import React, { FC, useCallback, useMemo } from 'react';

import type {
    ColDef,
    RowClassParams,
    RowClickedEvent,
    ValueGetterParams,
} from 'ag-grid-community';
import { useFormikContext } from 'formik';

// Components
import AGTable from '../../../../../components/ag-table/AGTable';
import styled from 'styled-components';

// Constants
import aampeTheme from '../../../../../constants/aampeTheme';

// Models
import { ElementKind } from 'models/editor.model';
import { FormulaTestMessageFormValues } from 'models/formula.model';
import { TestFormulaVariantData, VariantWithLabel } from 'models/variant.model';

interface TestFormulaElementVariantTableProps {
    alternateVariants: TestFormulaVariantData[];
    selectedVariant?: VariantWithLabel;
    setIsExpanded: (value: boolean) => void;
    type?: ElementKind;
}

const TestFormulaElementVariantTable: FC<TestFormulaElementVariantTableProps> =
    ({
        alternateVariants,
        selectedVariant,
        setIsExpanded,
        type = ElementKind.variant,
    }) => {
        const { setFieldValue, values } =
            useFormikContext<FormulaTestMessageFormValues>();

        const previewColumn = useMemo<ColDef<TestFormulaVariantData>[]>(() => {
            return type == ElementKind.image || type == ElementKind.section
                ? [
                      {
                          headerName: 'Preview',
                          cellRenderer: (
                              params: ValueGetterParams<
                                  TestFormulaVariantData,
                                  string
                              >,
                          ) => (
                              <AlternateImage
                                  src={params.data?.content ?? ''}
                              />
                          ),
                          minWidth: 100,
                          menuTabs: [],
                          resizable: false,
                      },
                  ]
                : [];
        }, [type]);

        const columns = useMemo<ColDef<TestFormulaVariantData>[]>(
            () => [
                ...previewColumn,
                {
                    headerName: 'Alternate',
                    valueGetter: (
                        params: ValueGetterParams<
                            TestFormulaVariantData,
                            string
                        >,
                    ) => params.data?.content ?? '',
                    minWidth: type === ElementKind.image ? 200 : 300,
                    maxWidth: type === ElementKind.image ? 300 : 400,
                    menuTabs: [],
                    resizable: true,
                    tooltipValueGetter: (params) =>
                        params.data?.isDisabled
                            ? 'This variant is unavailable due to label mismatch'
                            : '',
                },
                {
                    headerName: 'Label',
                    valueGetter: (
                        params: ValueGetterParams<
                            TestFormulaVariantData,
                            string
                        >,
                    ) => params.data?.label?.label_name ?? '',
                    minWidth: type === ElementKind.image ? 150 : 240,
                    flex: 1,
                    menuTabs: ['filterMenuTab'],
                    resizable: true,
                    filter: true,
                    tooltipValueGetter: (params) =>
                        params.data?.isDisabled
                            ? 'This variant is unavailable due to label mismatch'
                            : '',
                },
            ],
            [previewColumn, type],
        );

        const getRowStyle = useCallback(
            (params: RowClassParams<TestFormulaVariantData>) => {
                const isSelected = params.data?.id === selectedVariant?.id;
                return {
                    backgroundColor: isSelected
                        ? aampeTheme.colors.interactiveWG01
                        : aampeTheme.colors.white,
                };
            },
            [selectedVariant],
        );

        const onRowClicked = useCallback(
            (event: RowClickedEvent<TestFormulaVariantData>) => {
                const alternate: any = event.data;
                if (alternate?.isDisabled) {
                    return;
                }
                if (alternate) {
                    setFieldValue(
                        `filters.alternates.${alternate.component_id}`,
                        alternate.id,
                    );
                    setFieldValue('componentAttachedLabel', {
                        ...values.componentAttachedLabel,
                        [alternate.component_id]: {
                            componentTypeId: alternate.component_type_id,
                            labelId: alternate.label?.id ?? null,
                        },
                    });
                }
                setIsExpanded(false);
            },
            [setIsExpanded, setFieldValue, values],
        );

        const getRowClass = useCallback(
            (params: RowClassParams<TestFormulaVariantData>) => {
                if (params.data?.isDisabled) {
                    return 'variant-row--disabled';
                }
                return '';
            },
            [],
        );

        return (
            <StyledAGTable
                columns={columns}
                rowData={alternateVariants}
                tableStyle={{
                    maxHeight: '225px',
                    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
                    borderRadius: aampeTheme.borderRadius.default,
                }}
                getRowStyle={getRowStyle}
                onRowClicked={onRowClicked}
                getRowClass={getRowClass}
            />
        );
    };

export default TestFormulaElementVariantTable;

const StyledAGTable = styled(AGTable)({
    padding: 0,
    width: '100%',

    '.variant-row--disabled': {
        opacity: 0.45,
        cursor: 'not-allowed',
        pointerEvents: 'auto',
    },

    '.variant-row--disabled .ag-cell': {
        color: aampeTheme.colors.solidWG02,
    },
});

const AlternateImage = styled.img({
    borderRadius: aampeTheme.borderRadius.default,
    height: 30,
    width: 30,
    objectFit: 'contain',
});
